import styled from "@emotion/styled";
import dayjs from "dayjs";
import { BrandModifierDto, DisplayDateFormat2, i18n, useCalculationInput } from "src/common";
import { colors } from "src/theme";
import { Flex } from "../grid";
import { Accordion } from "../other/accordion.other";
import { AdditionalInfo } from "src/pages/app/offerPreview.page";
import { useState } from "react";
import { InputNumber, InputBirthDate, Switch } from "../inputs";
import { Card } from "./card";

const SectionHeader = styled.div`
  font-weight: 600;
  font-size: 12px;
  line-height: 16px;
  text-transform: uppercase;
  color: ${colors.text4};
`;

// Additional Info Section
const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
`;

const InfoBox = styled.div`
  background: rgba(242, 242, 242, 0.44);
  border-radius: 4px;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const InfoLabel = styled.span<{ color: string }>`
  font-weight: 400;
  font-size: 14px;
  line-height: 40px;
  color: ${({ color }) => color};
  overflow: hidden;
  text-overflow: ellipsis;
`;

const InfoValue = styled.span<{ color: string }>`
  font-weight: 400;
  font-size: 14px;
  line-height: 40px;
  color: ${({ color }) => color};
`;

const ChangeDataLink = styled.button`
  font-weight: 700;
  font-size: 16px;
  line-height: 20px;
  color: ${colors.primary};
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 8px;
  align-self: flex-end;
  text-align: right;

  &:hover {
    opacity: 0.8;
  }
`;

interface AdditionalInfoProps {
  additionalInfo: AdditionalInfo[]
  modifiers?: BrandModifierDto[]
}

export const AdditionalFilterCard: React.FC<AdditionalInfoProps> = ({
  additionalInfo,
  modifiers
}) => {

  const [expandAdditionalInfo, setExpandAdditionalInfo] = useState(false);
  const [additionalValues, setAdditionalValues] = useState(additionalInfo);
  const { setFilterData } = useCalculationInput();

  const handleExpandAdditionalInfo = () => {
    setExpandAdditionalInfo(!expandAdditionalInfo);
  }

  const handleAdditionalValues = (key: string, value: any) => {
    const item = additionalValues.map((v) => {
      if (v.key !== key) {
        return v;
      }
      return { key, value }
    });
    setAdditionalValues(item);
  }

  const handleSaveFilterData = () => {
    if(valuesChanged()) {
      setFilterData(additionalValues);
    }
    setExpandAdditionalInfo(false);
  }

  const valuesChanged = () => {
    return !(additionalInfo.every((item) =>
      additionalValues.find(i => i.key === item.key)?.value === item.value
    ));
  }

  const getPolicyDurationComponent = () => {
    if (modifiers) {
      const policyDurationModifier = modifiers.find(mod => mod.field === 'policyDuration');
      const duration = additionalValues.find(v => v.key === "policyDuration")?.value
      if (policyDurationModifier?.fixed) {
        return (
          <Switch
            options={[
              { value: 1, label: `1 ${i18n.t('components.cards.offerPreviewCard.additionalInfo.yearsAbbr')}` },
              { value: 99, label: i18n.t('components.cards.offerPreviewCard.additionalInfo.untilCancellation') }
            ]}
            value={duration}
            onChange={(value: boolean) => handleAdditionalValues("policyDuration", value)}
          />
        )
      } else if (policyDurationModifier?.min && policyDurationModifier?.max) {
        return (
          <Switch
            options={
              Array.from(
                { length: (policyDurationModifier.max) - (policyDurationModifier.min) + 1 },
                (_, i) =>
                  ({ value: (policyDurationModifier?.min || 0) + i, label: String((policyDurationModifier?.min || 0) + i)})
              )
            }
            value={duration}
            onChange={(value: string) => handleAdditionalValues("policyDuration", Number(value))}
          />
        )
      }
    }
    return (<InputNumber
      value={additionalValues.find(v => v.key === "policyDuration")?.value.toString()}
      onChange={(value: string) => handleAdditionalValues("policyDuration", Number(value))} placeholder={i18n.t('components.cards.offerPreviewCard.additionalInfo.placeholders.years')}
    />)
  }

  const additionalInfoComponent = (value: any): { [key: string]: string } => {
    const floorLabels: Record<string, string> = {
      groundFloor: i18n.t('components.cards.offerPreviewCard.additionalInfo.floor.groundFloor'),
      floor: i18n.t('components.cards.offerPreviewCard.additionalInfo.floor.floor'),
      loft: i18n.t('components.cards.offerPreviewCard.additionalInfo.floor.loft'),
      cellar: i18n.t('components.cards.offerPreviewCard.additionalInfo.floor.cellar'),
    };

    return {
      area: `${value}${i18n.t('components.cards.offerPreviewCard.additionalInfo.placeholders.area')}`,
      policyStartDate: dayjs(value).format(DisplayDateFormat2),
      policyDuration: value === 99 ? i18n.t('components.cards.offerPreviewCard.additionalInfo.untilCancellation')
        : `${value} ${i18n.t('components.cards.offerPreviewCard.additionalInfo.yearsAbbr')}`,
      usage: i18n.t(`components.cards.offerPreviewCard.additionalInfo.usage.${value}`),
      floor: floorLabels[value],
      isFlood: value ? "Da" : "Ne",
      masonry: value ? i18n.t('components.cards.offerPreviewCard.additionalInfo.masonry.masonry') : i18n.t('components.cards.offerPreviewCard.additionalInfo.masonry.wooden'),
      expenseExcedes: value ? "Da" : "Ne",
    };
  }

  const additionalEditComponent: Record<string, React.ReactNode> = {
    area: <InputNumber value={additionalValues.find(v => v.key === "area")?.value.toString()} onChange={(value: string) => handleAdditionalValues("area", Number(value))} placeholder={i18n.t('components.cards.offerPreviewCard.additionalInfo.placeholders.area')} />,
    policyStartDate: <InputBirthDate
      canBeLargeThanCurrentYear={true}
      canBeLessThanCurrentDate={false}
      value={dayjs(additionalValues.find(v => v.key === "policyStartDate")?.value).format('YYYY/MM/DD')}
      onChange={(value: string) => handleAdditionalValues("policyStartDate", dayjs(value)?.toISOString())}
    />,
    policyDuration: getPolicyDurationComponent(),
    // usage: (
    //   <Switch
    //   options={[
    //     { value: 'permanent', label: i18n.t('components.cards.offerPreviewCard.additionalInfo.usage.permanent') },
    //     // { value: 'temporary', label: i18n.t('components.cards.offerPreviewCard.additionalInfo.usage.temporary') },
    //     // { value: 'rental', label: i18n.t('components.cards.offerPreviewCard.additionalInfo.usage.rental') }
    //   ]}
    //   value={additionalValues.find(v => v.key === "usage")?.value}
    //   onChange={(value: string) => handleAdditionalValues("usage", value)}
    //   />
    // ),
    floor: (
      <Switch
      options={[
        { value: 'floor', label: i18n.t('components.cards.offerPreviewCard.additionalInfo.floor.floor') },
        { value: 'groundFloor', label: i18n.t('components.cards.offerPreviewCard.additionalInfo.floor.groundFloor') },
      ]}
      value={additionalValues.find(v => v.key === "floor")?.value[0]}
      onChange={(value: string[]) => handleAdditionalValues("floor", [value])}
      />
    ),
  }

  return (
    <Card padding={8}>
      <InfoSection>
        <SectionHeader>
          {i18n.t('components.cards.offerPreviewCard.additionalInfo.title')}
        </SectionHeader>
        <InfoBox>
          { additionalValues.map((info, index) => (
            <Accordion
              key={index}
              title={(
                <Flex justify='space-between' style={{ padding: "0 8px"}}>
                  <InfoLabel color={info.disabled ? colors.text4 : colors.text1}>{i18n.t(`components.cards.offerPreviewCard.additionalInfo.label.${info.key}`)}</InfoLabel>
                  <InfoValue color={info.disabled ? colors.text4 : colors.text1}>{additionalInfoComponent(info.value)[info.key]}</InfoValue>
                </Flex>
              )}
              content={additionalEditComponent[info.key] && !info.disabled && (
                <Flex vertical gap={8}>
                  {additionalEditComponent[info.key] || <></>}
                </Flex>
              ) || <></>}
              open={expandAdditionalInfo}
              expandable={false}
              onChange={() => undefined}
              expandIconComponent={<></>}
            />
          ))}
        </InfoBox>
        <Flex justify="flex-end">
          { !expandAdditionalInfo ?
            <ChangeDataLink onClick={handleExpandAdditionalInfo}>
              {i18n.t('components.cards.offerPreviewCard.additionalInfo.changeData')}
            </ChangeDataLink>
          : <ChangeDataLink onClick={handleSaveFilterData}>
              {i18n.t('components.cards.offerPreviewCard.additionalInfo.saveData')}
            </ChangeDataLink>
          }
        </Flex>
      </InfoSection>
    </Card>
  )
}
